import { Box, MenuItem, Select, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setDateRange } from 'app/slice/executive.slice'
import { formatDateRange, resolvePreset } from 'utils/executive.utils'
import type { ExecutiveState } from 'app/slice/executive.slice'
import type { DatePreset } from 'api/executive/executive.types'
import { EXEC_SELECT_MENU_PROPS, EXEC_SELECT_SX } from './filterInputStyles'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'

const PRESETS: DatePreset[] = [
  'today', 'yesterday', 'this-week', 'this-month',
  'last-month', 'ytd', 'custom',
]

export default function FilterDatePreset() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { preset, start, end } = useSelector(
    (s: { executive: ExecutiveState }) => s.executive.filters.dateRange
  )

  const handlePresetChange = (newPreset: DatePreset) => {
    if (newPreset === 'custom') {
      dispatch(setDateRange({ preset: 'custom', start, end }))
      return
    }
    const range = resolvePreset(newPreset)
    if (range) {
      dispatch(setDateRange({ preset: newPreset, ...range }))
    }
  }

  // Inline label + ⓘ help icon. The tooltip explains how the date filter
  // is interpreted by every widget and the one snapshot exception
  // (outstanding receivables).
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {t('EXECUTIVE.FILTER.DATE_RANGE')}
        </Typography>
        <Tooltip
          title={t('EXECUTIVE.FILTER.DESC.DATE_RANGE')}
          arrow
          placement="bottom"
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 360 } } }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      </Box>
      <Select
        size="small"
        value={preset}
        onChange={(e) => handlePresetChange(e.target.value as DatePreset)}
        MenuProps={EXEC_SELECT_MENU_PROPS}
        sx={{ minWidth: 180, ...EXEC_SELECT_SX }}
        // Show the resolved date range as helper text inside non-custom presets
        // so users can see exactly which dates they're looking at.
        renderValue={(v) => {
          const presetLabel = t(`EXECUTIVE.FILTER.DATE_PRESET.${String(v).toUpperCase().replace('-', '_')}`)
          if (v === 'custom') return presetLabel
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography component="span" variant="body2" sx={{ lineHeight: 1.2 }}>
                {presetLabel}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: '0.66rem', lineHeight: 1.1 }}
              >
                {start && end ? formatDateRange(start, end) : ''}
              </Typography>
            </Box>
          )
        }}
      >
        {PRESETS.map((p) => (
          <MenuItem key={p} value={p}>
            {t(`EXECUTIVE.FILTER.DATE_PRESET.${p.toUpperCase().replace('-', '_')}`)}
          </MenuItem>
        ))}
      </Select>
      {preset === 'custom' && (
        // System-wide range picker (used by /dashboard etc.) — Buddhist
        // calendar, dual-month popup, allowClear. Wraps the slice's ISO
        // start/end into the [start, end] array shape the component
        // consumes; the onchange callback converts back via moment.
        <Box sx={{ minWidth: 260 }}>
          <InputSecondNewRangePicker
            inputHeight={32}
            value={start && end ? [start, end] : emptyRangePicker}
            onchange={(val: any) => {
              // Component fires onchange on EVERY click — first click yields
              // [Date, ''], second [Date, Date]. Only commit when both ends
              // are present so the picker stays interactive mid-selection.
              if (!Array.isArray(val) || !val[0] || !val[1]) return
              const nextStart = moment(val[0]).format('YYYY-MM-DD')
              const nextEnd = moment(val[1]).format('YYYY-MM-DD')
              dispatch(setDateRange({ preset: 'custom', start: nextStart, end: nextEnd }))
            }}
            allowClear
            onClear={() => {
              // Clearing the range while in custom mode → reset to this-month
              // so the global filter never lands in an invalid empty state.
              const next = resolvePreset('this-month')
              if (next) dispatch(setDateRange({ preset: 'this-month', ...next }))
            }}
          />
        </Box>
      )}
    </Box>
  )
}
